import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronRight, Eye } from 'lucide-react';

var fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8 }
};

var categories = [
  { id: 'breast-surgery', title: 'Breast Surgery' },
  { id: 'body-contouring', title: 'Body Contouring' }
];

var procedures = [
  { id: 'breast-augmentation', catId: 'breast-surgery', name: 'Breast Augmentation' },
  { id: 'augmentation-mastopexy', catId: 'breast-surgery', name: 'Augmentation Mastopexy' },
  { id: 'breast-reduction', catId: 'breast-surgery', name: 'Breast Reduction' },
  { id: 'mastopexy', catId: 'breast-surgery', name: 'Mastopexy' },
  { id: 'fleur-de-lys-abdominoplasty', catId: 'body-contouring', name: 'Fleur de Lys Abdominoplasty' },
  { id: 'abdominoplasty', catId: 'body-contouring', name: 'Abdominoplasty' },
  { id: 'medial-thigh-lift', catId: 'body-contouring', name: 'Medial Thigh Lift' }
];

var cases = [
  { id: 'ba-1', procId: 'breast-augmentation', before: '/ba-breast-aug-before.webp', after: '/ba-breast-aug-after.webp' },
  { id: 'br-1', procId: 'breast-reduction', before: '/ba-breast-red-before.webp', after: '/ba-breast-red-after.webp' },
  { id: 'abdo-front', procId: 'fleur-de-lys-abdominoplasty', before: '/ba-abdo-before-front.webp', after: '/ba-abdo-after-front.webp', description: 'Front view' },
  { id: 'abdo-side', procId: 'fleur-de-lys-abdominoplasty', before: '/ba-abdo-before-side.webp', after: '/ba-abdo-after-side.webp', description: 'Side view' },
  { id: 'thigh-front', procId: 'medial-thigh-lift', before: '/ba-thigh-before-front.webp', after: '/ba-thigh-after-front.webp', description: 'Front view' },
  { id: 'thigh-side', procId: 'medial-thigh-lift', before: '/ba-thigh-before-side.webp', after: '/ba-thigh-after-side.webp', description: 'Side view' },
  { id: 'abdo2-front', procId: 'abdominoplasty', before: '/ba-abdo2-before-front.webp', after: '/ba-abdo2-after-front.webp', description: 'Front view' },
  { id: 'abdo2-side', procId: 'abdominoplasty', before: '/ba-abdo2-before-side.webp', after: '/ba-abdo2-after-side.webp', description: 'Side view' }
];

function getCatTitle(catId) {
  for (var i = 0; i < categories.length; i++) {
    if (categories[i].id === catId) return categories[i].title;
  }
  return '';
}

function getProcName(procId) {
  for (var i = 0; i < procedures.length; i++) {
    if (procedures[i].id === procId) return procedures[i].name;
  }
  return '';
}

function GalleryCase(props) {
  var item = props.item;
  var revealState = useState(false);
  var revealed = revealState[0];
  var setRevealed = revealState[1];

  return (
    <div className="bg-white border border-clinic-border" data-testid={'gallery-item-' + item.id}>
      <div className="relative">
        <div className="grid grid-cols-2">
          <div className="relative overflow-hidden border-r border-clinic-border">
            <div className="absolute top-4 left-4 z-20 px-3 py-1 bg-clinic-charcoal text-clinic-bg text-xs font-body tracking-widest uppercase">Before</div>
            <div className="aspect-[3/4]">
              <img src={item.before} alt="Before" className={'w-full h-full object-cover transition-all duration-500 ' + (revealed ? '' : 'blur-xl scale-105')} />
            </div>
          </div>
          <div className="relative overflow-hidden">
            <div className="absolute top-4 left-4 z-20 px-3 py-1 bg-clinic-charcoal text-clinic-bg text-xs font-body tracking-widest uppercase">After</div>
            <div className="aspect-[3/4]">
              <img src={item.after} alt="After" className={'w-full h-full object-cover transition-all duration-500 ' + (revealed ? '' : 'blur-xl scale-105')} />
            </div>
          </div>
        </div>

        {!revealed && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="text-center px-8">
              <p className="font-body text-clinic-charcoal text-sm mb-4 max-w-sm mx-auto">
                These images contain clinical photographs. Click below to view.
              </p>
              <button
                onClick={function() { setRevealed(true); }}
                className="btn-primary inline-flex items-center gap-2"
                data-testid={'reveal-' + item.id}
              >
                <Eye size={16} />
                View Images
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BeforeAfterPage() {
  var searchState = useSearchParams();
  var searchParams = searchState[0];
  var setSearchParams = searchState[1];

  var selCat = searchParams.get('cat');
  var selProc = searchParams.get('proc');

  var view = 'categories';
  if (selCat && selProc) view = 'gallery';
  else if (selCat) view = 'procedures';

  function goToProcs(catId) {
    setSearchParams({ cat: catId });
  }

  function goToGallery(procId) {
    setSearchParams({ cat: selCat, proc: procId });
  }

  function backToCats() {
    setSearchParams({});
  }

  function backToProcs() {
    setSearchParams({ cat: selCat });
  }

  var filteredProcs = procedures.filter(function(p) { return p.catId === selCat; });
  var filteredCases = cases.filter(function(c) { return c.procId === selProc; });

  return (
    <div className="bg-clinic-bg" data-testid="before-after-page">
      <section className="pt-32 pb-20 md:pt-40 md:pb-28" data-testid="gallery-hero">
        <div className="container-clinic">
          <motion.div initial="initial" animate="animate" variants={{ animate: { transition: { staggerChildren: 0.1 } } }} className="max-w-3xl">
            <motion.p variants={fadeInUp} className="text-caption mb-4">Results</motion.p>
            <motion.h1 variants={fadeInUp} className="text-heading-1 text-clinic-charcoal mb-8">Before & After</motion.h1>
            <motion.p variants={fadeInUp} className="text-body-lg">
              All photographs are shared with full patient consent. Results vary between individuals and are shown to illustrate the type of outcomes that may be achieved.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="pb-20 md:pb-32" data-testid="gallery-section">
        <div className="container-clinic">

          {view === 'categories' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map(function(cat) {
                return (
                  <button key={cat.id} onClick={function() { goToProcs(cat.id); }} className="group p-8 bg-white border border-clinic-border hover:border-clinic-charcoal transition-colors text-left" data-testid={'category-' + cat.id}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-heading-3 text-clinic-charcoal mb-2 group-hover:text-clinic-stone transition-colors">{cat.title}</h3>
                      </div>
                      <ChevronRight size={22} className="text-clinic-stone group-hover:text-clinic-charcoal group-hover:translate-x-1 transition-all" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {view === 'procedures' && (
            <div>
              <button onClick={backToCats} className="inline-flex items-center gap-2 text-sm font-body text-clinic-stone hover:text-clinic-charcoal transition-colors mb-10" data-testid="back-to-categories">
                <ArrowLeft size={16} /> All Categories
              </button>
              <h2 className="text-heading-2 text-clinic-charcoal mb-10">{getCatTitle(selCat)}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredProcs.map(function(proc) {
                  return (
                    <button key={proc.id} onClick={function() { goToGallery(proc.id); }} className="group p-8 bg-white border border-clinic-border hover:border-clinic-charcoal transition-colors text-left" data-testid={'procedure-' + proc.id}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-heading-3 text-clinic-charcoal mb-2 group-hover:text-clinic-stone transition-colors">{proc.name}</h3>
                        </div>
                        <ChevronRight size={22} className="text-clinic-stone group-hover:text-clinic-charcoal group-hover:translate-x-1 transition-all" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {view === 'gallery' && (
            <div>
              <button onClick={backToProcs} className="inline-flex items-center gap-2 text-sm font-body text-clinic-stone hover:text-clinic-charcoal transition-colors mb-10" data-testid="back-to-procedures">
                <ArrowLeft size={16} /> Back to {getCatTitle(selCat)}
              </button>
              <h2 className="text-heading-2 text-clinic-charcoal mb-10">{getProcName(selProc)}</h2>

              {filteredCases.length === 0 && (
                <div className="text-center py-16 px-8 bg-white border border-clinic-border">
                  <h3 className="text-heading-3 text-clinic-charcoal mb-4">Coming soon</h3>
                  <p className="text-body-lg max-w-md mx-auto mb-8">Before and after photographs for this procedure will be added with full patient consent.</p>
                  <Link to="/contact" className="btn-primary">Request a Consultation</Link>
                </div>
              )}

              {filteredCases.length > 0 && (
                <div className="space-y-16">
                  {filteredCases.map(function(item) {
                    return (
                      <GalleryCase key={item.id} item={item} />
                    );
                  })}
                </div>
              )}
            </div>
          )}

        </div>
      </section>

      <section className="section-padding bg-clinic-muted" data-testid="gallery-disclaimer">
        <div className="container-clinic">
          <div className="max-w-3xl">
            <h2 className="text-heading-3 text-clinic-charcoal mb-4">Important information</h2>
            <p className="text-body-lg mb-4">All before and after photographs are published with the express written consent of the patient. Images are unaltered and represent genuine results.</p>
            <p className="text-body-lg">Individual results may vary. Before and after images do not constitute a guarantee of results.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
